import { getInitData } from "./telegram.ts";

/**
 * Minimal API client for the Mini App. Establishes a session by sending the
 * Telegram initData to the backend (which validates it — the trust boundary),
 * then carries the returned JWT on subsequent calls.
 *
 * Base URL: VITE_API_URL if set, else same-origin (the Vite dev server proxies
 * /api to the backend; in production the host serves both under one origin).
 */
const API_BASE = import.meta.env.VITE_API_URL ?? "";

export type VerificationStatus =
  | "unverified"
  | "pending"
  | "verified"
  | "failed"
  | "expired";

export interface UserProfile {
  nickname: string | null;
  email: string | null;
  phone: string | null;
  complete: boolean;
}

export interface PublicUser {
  id: string;
  telegramId: string;
  username: string | null;
  firstName: string | null;
  verificationStatus: VerificationStatus;
  verifiedAt: string | null;
  verificationExpiry: string | null;
  profile: UserProfile;
  stampCount: number;
  freeVapeCredits: number;
  referralCode: string | null;
  referred: boolean;
}

/** Apply a friend's referral code (once, before first order). */
export async function applyReferral(code: string): Promise<PublicUser> {
  const res = await authedFetch("/api/account/referral", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { user: PublicUser };
  return data.user;
}

export interface ProfileInput {
  nickname?: string | null;
  email?: string | null;
  phone?: string | null;
}

/** Create/update the optional customer profile (nickname / email / phone). */
export async function updateProfile(input: ProfileInput): Promise<PublicUser> {
  const res = await authedFetch("/api/account", {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { user: PublicUser };
  return data.user;
}

let sessionToken: string | null = null;

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/** Exchange Telegram initData for a session token. Returns the current user. */
export async function createSession(): Promise<PublicUser> {
  const initData = getInitData();
  if (!initData) {
    throw new ApiError(0, "no Telegram initData (open from inside Telegram)");
  }

  const res = await fetch(`${API_BASE}/api/session`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ initData }),
  });
  if (!res.ok) {
    throw new ApiError(res.status, await errorMessage(res));
  }
  const data = (await res.json()) as { token: string; user: PublicUser };
  sessionToken = data.token;
  return data.user;
}

/** fetch wrapper that attaches the session token. */
export async function authedFetch(
  path: string,
  init: RequestInit = {},
): Promise<Response> {
  if (!sessionToken) throw new ApiError(401, "no session");
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${sessionToken}`);
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

export async function getMe(): Promise<PublicUser> {
  const res = await authedFetch("/api/me");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { user: PublicUser };
  return data.user;
}

export interface PublicVariant {
  id: string;
  name: string;
  /** Flavor photo; the API falls back to the product image when unset. */
  imageUrl: string;
  priceCents: number;
  inStock: boolean;
  availableStock: number;
}

export interface PublicProduct {
  id: string;
  name: string;
  description: string;
  category: string;
  priceCents: number;
  currency: string;
  imageUrl: string;
  inStock: boolean;
  availableStock: number;
  rating: number | null;
  ratingCount: number;
  brand: string | null;
  flavor: string | null;
  nicotineMg: number | null;
  badge: string | null;
  variants: PublicVariant[];
}

// --- Favorites ---
export async function getFavorites(): Promise<string[]> {
  const res = await authedFetch("/api/favorites");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { productIds: string[] };
  return data.productIds;
}
export async function addFavorite(productId: string): Promise<void> {
  const res = await authedFetch(`/api/favorites/${encodeURIComponent(productId)}`, {
    method: "POST",
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
}
export async function removeFavorite(productId: string): Promise<void> {
  const res = await authedFetch(`/api/favorites/${encodeURIComponent(productId)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
}

// --- Saved addresses ---
export interface SavedAddress {
  id: string;
  label: string | null;
  recipient: string;
  address: string;
  contact: string;
  lat: number | null;
  lng: number | null;
  isDefault: boolean;
}
export interface AddressInput {
  label?: string | null;
  recipient: string;
  address: string;
  contact: string;
  lat?: number | null;
  lng?: number | null;
  isDefault?: boolean;
}
export async function getAddresses(): Promise<SavedAddress[]> {
  const res = await authedFetch("/api/addresses");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return ((await res.json()) as { addresses: SavedAddress[] }).addresses;
}
export async function saveAddress(input: AddressInput): Promise<SavedAddress> {
  const res = await authedFetch("/api/addresses", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return ((await res.json()) as { address: SavedAddress }).address;
}
export async function deleteAddress(id: string): Promise<void> {
  const res = await authedFetch(`/api/addresses/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
}

// --- Promo codes + points ---
export interface PromoPreview {
  valid: boolean;
  reason?: string;
  code?: string;
  kind?: string;
  discountCents?: number;
  newTotalCents?: number;
}
export async function validatePromo(
  code: string,
  items: { productId: string; quantity: number }[],
): Promise<PromoPreview> {
  const res = await authedFetch("/api/promo/validate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, items }),
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return (await res.json()) as PromoPreview;
}

// --- Reviews ---
export interface Review {
  id: string;
  rating: number;
  comment: string | null;
  author: string;
  createdAt: string;
  mine: boolean;
}
export async function getReviews(
  productId: string,
): Promise<{ reviews: Review[]; canReview: boolean }> {
  const res = await authedFetch(`/api/products/${encodeURIComponent(productId)}/reviews`);
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return (await res.json()) as { reviews: Review[]; canReview: boolean };
}
export async function submitReview(
  productId: string,
  rating: number,
  comment: string | null,
): Promise<void> {
  const res = await authedFetch(`/api/products/${encodeURIComponent(productId)}/reviews`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ rating, comment }),
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
}

export type CartLineIssue = "unavailable" | "insufficient_stock";

export interface PricedCartLine {
  productId: string;
  variantId: string | null;
  name: string;
  variantName: string | null;
  priceCents: number;
  quantity: number;
  lineTotalCents: number;
  availableStock: number;
  issue?: CartLineIssue;
}

export interface AppliedBundle {
  bundleId: string;
  name: string;
  sets: number;
  discountCents: number;
}

export interface PricedCart {
  lines: PricedCartLine[];
  subtotalCents: number;
  bundleDiscountCents: number;
  bundles: AppliedBundle[];
  deliveryCents: number;
  totalCents: number;
  currency: string;
  valid: boolean;
}

export interface StorefrontConfig {
  currency: string;
  minAge: number;
  deliveryFeeCents: number;
  freeDeliveryThresholdCents: number;
  deliveryEtaMinutes: number;
  wheelMinOrderCents: number;
  minOrderCents: number;
  stampCardSize: number;
  stampRewardMaxCents: number;
  stampCategories: string[];
  store: {
    hoursEnabled: boolean;
    open: boolean;
    paused: boolean;
    pausedMessage: string;
    preorderEnabled: boolean;
    opensAt: string;
    closesAt: string;
  };
}

/** Storefront business config (delivery fee/ETA, thresholds) for UI nudges. */
export async function getConfig(): Promise<StorefrontConfig> {
  const res = await authedFetch("/api/config");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { config: StorefrontConfig };
  return data.config;
}

export async function getProducts(): Promise<PublicProduct[]> {
  const res = await authedFetch("/api/products");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { products: PublicProduct[] };
  return data.products;
}

export interface CartItem {
  productId: string;
  variantId?: string | null;
  quantity: number;
}

/** Load the user's durable cart (shared with the bot's /menu). */
export async function getCartItems(): Promise<CartItem[]> {
  const res = await authedFetch("/api/cart/items");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return ((await res.json()) as { items: CartItem[] }).items;
}

/** Persist the cart so it survives a webview reload (best-effort; never throws). */
export async function saveCartItems(items: CartItem[]): Promise<void> {
  try {
    await authedFetch("/api/cart/items", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });
  } catch {
    /* transient — the cart also lives in component state */
  }
}

export async function priceCart(items: CartItem[]): Promise<PricedCart> {
  const res = await authedFetch("/api/cart", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { cart: PricedCart };
  return data.cart;
}

export interface GeoPlace {
  lat: number;
  lng: number;
  address: string;
}

/** Reverse-geocode map-picked coordinates to an address (best-effort; null on miss). */
export async function reverseGeocode(
  lat: number,
  lng: number,
): Promise<GeoPlace | null> {
  const res = await authedFetch(
    `/api/geocode/reverse?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(lng)}`,
  );
  if (!res.ok) return null;
  const data = (await res.json()) as { place: GeoPlace | null };
  return data.place;
}

export interface DeliveryDetails {
  name: string;
  address: string;
  contact: string;
  window?: string;
  notes?: string;
  lat?: number;
  lng?: number;
}

export interface PublicOrder {
  id: string;
  status: string;
  paymentMethod: string;
  subtotalCents: number;
  deliveryCents: number;
  discountCents: number;
  stampsEarned: number;
  freeVapeCents: number;
  totalCents: number;
  currency: string;
  delivery: {
    name: string;
    address: string;
    contact: string;
    window: string | null;
    notes: string | null;
    lat: number | null;
    lng: number | null;
  };
  preorder: boolean;
  cancelReason: string | null;
  items: {
    productId: string;
    variantId: string | null;
    name: string;
    variantName: string | null;
    priceCents: number;
    quantity: number;
  }[];
  spin: {
    eligible: boolean;
    spun: boolean;
    prizeId: string | null;
    prizeLabel: string | null;
  };
  createdAt: string;
  expiresAt: string | null;
}

export interface WheelSegment {
  id: string;
  label: string;
  color: string;
}

export interface SpinResult {
  wonId: string;
  label: string;
  alreadySpun: boolean;
}

/** Fetch the prize segments so the client draws the same wheel the server picks from. */
export async function getWheelConfig(): Promise<WheelSegment[]> {
  const res = await authedFetch("/api/wheel");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { segments: WheelSegment[] };
  return data.segments;
}

/** Spin the wheel for a qualifying order. Server picks the prize; one-shot. */
export async function spinWheel(orderId: string): Promise<SpinResult> {
  const res = await authedFetch(
    `/api/orders/${encodeURIComponent(orderId)}/spin`,
    { method: "POST" },
  );
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return (await res.json()) as SpinResult;
}

export interface CreateOrderInput {
  items: CartItem[];
  delivery: DeliveryDetails;
  promoCode?: string;
  redeemFreeVape?: boolean;
}

/** Create a prepaid order. `idempotencyKey` dedupes retries (pass a stable UUID). */
export async function createOrder(
  input: CreateOrderInput,
  idempotencyKey: string,
): Promise<PublicOrder> {
  const res = await authedFetch("/api/orders", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Idempotency-Key": idempotencyKey,
    },
    body: JSON.stringify({ ...input, ackIdOnDelivery: true }),
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { order: PublicOrder };
  return data.order;
}

/** The current user's recent orders (history / reorder). */
export async function listOrders(): Promise<PublicOrder[]> {
  const res = await authedFetch("/api/orders");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { orders: PublicOrder[] };
  return data.orders;
}

export async function getOrder(id: string): Promise<PublicOrder> {
  const res = await authedFetch(`/api/orders/${encodeURIComponent(id)}`);
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { order: PublicOrder };
  return data.order;
}

/** Cancel the user's own order while it's still pending. */
export async function cancelOrder(id: string): Promise<PublicOrder> {
  const res = await authedFetch(`/api/orders/${encodeURIComponent(id)}/cancel`, {
    method: "POST",
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return ((await res.json()) as { order: PublicOrder }).order;
}

// --- Stamp card ---
export interface StampInfo {
  count: number;
  size: number;
  freeVapeCredits: number;
  remaining: number;
}

/** Account + stamp-card summary (GET /api/account). */
export async function getAccount(): Promise<{ user: PublicUser; stamps: StampInfo }> {
  const res = await authedFetch("/api/account");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return (await res.json()) as { user: PublicUser; stamps: StampInfo };
}

// --- Back-in-stock alerts ---
export interface StockAlert {
  productId: string;
  variantId: string | null;
}
export async function getStockAlerts(): Promise<StockAlert[]> {
  const res = await authedFetch("/api/stock-alerts");
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return ((await res.json()) as { alerts: StockAlert[] }).alerts;
}
export async function toggleStockAlert(
  productId: string,
  variantId?: string | null,
): Promise<boolean> {
  const res = await authedFetch("/api/stock-alerts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ productId, variantId: variantId ?? null }),
  });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return ((await res.json()) as { subscribed: boolean }).subscribed;
}

export interface StartVerificationResult {
  status: VerificationStatus;
  redirectUrl: string | null;
  devComplete: boolean;
}

/** Begin age/identity verification. */
export async function startVerification(): Promise<StartVerificationResult> {
  const res = await authedFetch("/api/verify/start", { method: "POST" });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  return (await res.json()) as StartVerificationResult;
}

/** DEV ONLY (mock provider): approve the current pending verification. */
export async function devCompleteVerification(): Promise<PublicUser> {
  const res = await authedFetch("/api/verify/dev-complete", { method: "POST" });
  if (!res.ok) throw new ApiError(res.status, await errorMessage(res));
  const data = (await res.json()) as { user: PublicUser };
  return data.user;
}

export function hasSession(): boolean {
  return sessionToken !== null;
}

async function errorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error ?? res.statusText;
  } catch {
    return res.statusText;
  }
}
